import React, { useState, useCallback, useEffect } from 'react';
import { useLazyQuery } from 'react-apollo';
import { withRouter } from 'react-router';
import classnames from 'classnames';
import partial from 'lodash/partial';
import { Enums } from '@letsdothis/tracking';

import { UserNotificationQuery } from '@services/gql/user';
import { useAuthService } from '@/contexts/authService';
import { useTracking, withTrackingSource } from '@/tracking';
import Icon, { Icons } from '@shared/Icons/Icon';
import history from '@/routes/routes/helpers/history';

import NotificationPanel from '../components/NotificationPanel';
import { TRACKING_SOURCE, NOTIFICATIONS_PATH } from '../constants';
import { isMobileOrTablet } from '../../../utils/browserDetection';
import useNotifications from '../hooks/useNotifications';

import * as styles from './styles.module.css';

const NotificationIcon = (props) => {
  const { componentClicked } = useTracking();
  const [panelOpen, setPanelOpen] = useState(false);
  const authService = useAuthService();
  const loggedIn = authService.loggedIn();
  const userCookie = authService.getUserCookie();
  const skip = !userCookie || !__CLIENT__;
  const [fetch, { data, refetch }] = useLazyQuery(UserNotificationQuery, {
    skip,
    fetchPolicy: 'cache-and-network',
    variables: { primaryIdentityId: userCookie },
  });
  // We only want to refetch this data when notification center is closed
  // to avoid notifications switching between lists
  const refetchIfClosed = useCallback(() => {
    if (!panelOpen && !skip && refetch) {
      refetch();
    }
  }, [panelOpen]);

  const { newNotificationLength } = useNotifications(data?.user?.notifications);

  const setPanelOpenWithTracking = (open) => {
    componentClicked({
      short_name: open ? 'open_notification_list' : 'close_notification_list',
      component_type: Enums.ComponentClickedComponentType.Button,
      action: open
        ? Enums.ComponentClickedAction.Opened
        : Enums.ComponentClickedAction.Closed,
    });
    setPanelOpen(open);
  };

  const onIconClick = (open) => {
    if (props.location.pathname === NOTIFICATIONS_PATH) {
      refetch();
    } else if (isMobileOrTablet()) {
      history.push({ pathname: NOTIFICATIONS_PATH });
    } else {
      setPanelOpenWithTracking(open);
    }
  };
  const onNotificationClick = () => {
    setPanelOpen(false);
  };

  useEffect(() => {
    if (!skip) {
      fetch();
    }
  }, []);

  useEffect(() => {
    refetchIfClosed();
  }, [panelOpen]);

  return !loggedIn ? null : (
    <>
      <div className={styles.bubbleContainer}>
        <Icon
          icon={Icons.BELL.BLACK}
          onClick={partial(onIconClick, !panelOpen)}
          onKeyPress={partial(onIconClick, !panelOpen)}
          className={classnames('ignore-react-onclickoutside', styles.icon)}
        />
        {newNotificationLength > 0 && (
          <span className={styles.bubble}>
            {newNotificationLength > 9 ? '9+' : newNotificationLength}
          </span>
        )}
      </div>
      {panelOpen && (
        <NotificationPanel
          setPanelOpen={setPanelOpenWithTracking}
          onNotificationClick={onNotificationClick}
        />
      )}
    </>
  );
};

export default React.memo(
  withRouter(withTrackingSource(TRACKING_SOURCE, NotificationIcon)),
);
